import React, { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Session } from 'types/Session';
import { InputTextArea } from 'components/_new/InputTextArea';
import { Button } from 'components/_new/Button';
import { Box, BoxSection } from 'components/_new/Box';
import { MessageBox, MessageBoxRef } from 'components/_new/MessageBox';
import { SessionHomeworkPayload } from 'types/payloads/SessionPayload';
import { LinkDocArticleWithIconWrapper } from '../components/LinkDocArticleWithIconWrapper';

type HomeworkSubpageProps = {
	session: Session;
	saveHandler: (data: Partial<SessionHomeworkPayload>, showToast?: boolean) => any;
	loading: boolean;
};

type HomeworkForm = {
	mostImportant: string;
	practice: string;
	homework: string;
};

export const HomeworkSubpage = ({ session, saveHandler, loading }: HomeworkSubpageProps) => {
	const { t } = useTranslation();

	const autosaveMessageRefs = useRef<Record<string, MessageBoxRef>>({});

	const defaultValues: HomeworkForm = useMemo(
		() => ({
			mostImportant: session.mostImportant || '',
			practice: session.practice || '',
			homework: session.homework || '',
		}),
		[session.mostImportant, session.practice, session.homework]
	);

	const { control, handleSubmit, reset, getValues } = useForm<HomeworkForm>({ defaultValues });

	useEffect(() => {
		reset(defaultValues);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultValues]);

	const handleSubmitForm = handleSubmit(({ mostImportant, homework }) => {
		saveHandler(
			{
				mostImportant,
				homework,
			},
			true
		);
	});

	const autosave = (fieldName: keyof HomeworkForm) => {
		const value = getValues(fieldName);
		if (value !== defaultValues[fieldName]) {
			saveHandler(
				{
					[fieldName]: value,
				},
				false
			);
			autosaveMessageRefs.current[fieldName]?.show();
		}
	};

	return (
		<form className="flex flex-column gap-2" onSubmit={handleSubmitForm}>
			<h2 className="my-5">{t('userPanel.sessionsNew.homework.heading')}</h2>
			<p>
				<Trans
					i18nKey="userPanel.sessionsNew.homework.desc"
					components={[<LinkDocArticleWithIconWrapper articleLabel="homework" />]}
				/>
			</p>

			<Box variant="white-bordered">
				<Controller
					name="mostImportant"
					control={control}
					render={({ field: { value, ...restField } }) => (
						<BoxSection
							header={t('userPanel.sessionsNew.homework.mostImprtantField')}
							headerClassName="pt-2 font-semibold heading-16-24 text-on-blue-grey"
							contentClassName="pb-2"
						>
							<InputTextArea
								rows={3}
								{...restField}
								value={value}
								defaultValue={value}
								onBlur={() => {
									autosave('mostImportant');
								}}
							/>
							<MessageBox
								ref={(el: MessageBoxRef) => {
									if (el) {
										autosaveMessageRefs.current.mostImportant = el;
									}
								}}
								message={t('userPanel.sessionsNew.reflection.autosaveMessage')}
								variant="success"
								autoHide
								autoHideDuration={3000}
								className="mb-2"
								tiny
							/>
						</BoxSection>
					)}
				/>
			</Box>

			<Box variant="white-bordered">
				<Controller
					name="homework"
					control={control}
					render={({ field: { value, ...restField } }) => (
						<BoxSection
							header={t('userPanel.sessionsNew.homework.homeworkField')}
							headerClassName="pt-2 font-semibold heading-16-24 text-on-blue-grey"
							contentClassName="pb-2"
						>
							<MessageBox
								ref={(el: MessageBoxRef) => {
									if (el) {
										autosaveMessageRefs.current.homework = el;
									}
								}}
								message={t('userPanel.sessionsNew.reflection.autosaveMessage')}
								variant="success"
								autoHide
								autoHideDuration={3000}
								className="mb-2"
								tiny
							/>
							<InputTextArea
								rows={3}
								{...restField}
								value={value}
								defaultValue={value}
								onBlur={() => {
									autosave('homework');
								}}
							/>
						</BoxSection>
					)}
				/>
			</Box>

			<div className="flex flex-row justify-content-end">
				<Button submit label={t('actions.save')} loading={loading} />
			</div>
		</form>
	);
};
